/**
 * Accepts only a same-app path ("/x", optionally with query/hash) for use as a
 * redirect target. Rejects absolute URLs, protocol-relative ("//host") and
 * backslash network-path ("/\host") forms — WHATWG URL parsing normalizes
 * backslashes to slashes for http(s), so "/\evil.example" navigates off-origin
 * (open redirect) despite passing a naive startsWith("//") check.
 *
 * Pure and dependency-free: safe in server components, "use client" code and
 * SSR (no window). Shared by the auth pages' redirect_url handling and the
 * signup widget's first-party sign-in link.
 */
const INTERNAL_BASE = "https://internal.invalid";

export function safeInternalPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  try {
    if (new URL(value, INTERNAL_BASE).origin !== INTERNAL_BASE) return null;
  } catch {
    return null;
  }
  return value;
}

export type AuthRedirectFlow = "sign-in" | "sign-up";

/**
 * The query keys Clerk accepts as a post-auth destination, PER FLOW and in
 * Clerk's own precedence order: force → `redirect_url` → fallback → the legacy
 * `after_*` alias.
 *
 * Flow-specific on purpose. A single global ordering picks the wrong
 * destination when a URL carries controls for both flows — `/login?…&
 * sign_up_force_redirect_url=/signup-target&sign_in_fallback_redirect_url=
 * /signin-target` would resolve to the SIGN-UP target and the login page would
 * then apply it as its sign-in destination. clerk-js resolves each chain
 * independently, so we must too.
 *
 * Reading only `redirect_url` (the original behaviour) silently dropped the
 * destination whenever a link carried an alias instead: the user completed
 * sign-in and landed on the dashboard rather than the checkout or invite they
 * were sent to.
 */
export const AUTH_REDIRECT_PARAM_KEYS_BY_FLOW: Record<
  AuthRedirectFlow,
  readonly string[]
> = {
  "sign-in": [
    "sign_in_force_redirect_url",
    "redirect_url",
    "sign_in_fallback_redirect_url",
    "after_sign_in_url",
  ],
  "sign-up": [
    "sign_up_force_redirect_url",
    "redirect_url",
    "sign_up_fallback_redirect_url",
    "after_sign_up_url",
  ],
};

/**
 * Every key either flow accepts. Used to hold this list in step with the
 * redirect controls `lib/analytics/suppressed-paths.ts` already scrubs.
 */
export const AUTH_REDIRECT_PARAM_KEYS = [
  ...new Set([
    ...AUTH_REDIRECT_PARAM_KEYS_BY_FLOW["sign-in"],
    ...AUTH_REDIRECT_PARAM_KEYS_BY_FLOW["sign-up"],
  ]),
] as const;

/**
 * First safe internal path among the keys THIS flow accepts, or null. Every
 * candidate still goes through `safeInternalPath`, so an alias cannot smuggle
 * in an off-origin target that `redirect_url` would have rejected.
 */
export function firstSafeAuthRedirect(
  params: Record<string, string | string[] | undefined> | undefined,
  flow: AuthRedirectFlow,
): string | null {
  if (!params) return null;
  for (const key of AUTH_REDIRECT_PARAM_KEYS_BY_FLOW[flow]) {
    const raw = params[key];
    const candidate = Array.isArray(raw) ? raw[0] : raw;
    const safe = safeInternalPath(candidate);
    if (safe) return safe;
  }
  return null;
}
